import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppShell from './components/AppShell';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ImportPage from './pages/ImportPage';
import ImportReconciliationPage from './pages/ImportReconciliationPage';
import InvoicesPage from './pages/InvoicesPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import MissingInvoicesPage from './pages/MissingInvoicesPage';
import PartiesPage from './pages/PartiesPage';
import SalesmenPage from './pages/SalesmenPage';
import ProductsPage from './pages/ProductsPage';
import TransportsPage from './pages/TransportsPage';
import StationsPage from './pages/StationsPage';
import BiltyDocsPage from './pages/BiltyDocsPage';
import LogsPage from './pages/LogsPage';
import SettingsPage from './pages/SettingsPage';
import UserManagementPage from './pages/UserManagementPage';
import MyPortalPage from './pages/MyPortalPage';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-boss-violet border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-boss-violet border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin()) return <Navigate to="/boss/home" replace />;
  return children;
}

function NavGuardRoute({ children, path }) {
  const { user, loading, isNavAllowed } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-boss-violet border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  // path may be a single route, or an array for merged pages that combine
  // sections gated by different original permissions (e.g. Bilty Docs) —
  // allowed if the user has any one of them.
  const paths = Array.isArray(path) ? path : [path];
  if (!paths.some(isNavAllowed)) return <Navigate to="/boss/home" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-boss-violet border-t-transparent rounded-full" /></div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/boss/home" replace /> : <LoginPage />} />
      <Route path="/boss" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route path="home" element={<DashboardPage />} />
        {/* Finance & Accounting */}
        <Route path="fa/import" element={<NavGuardRoute path="/boss/fa/import"><ImportPage /></NavGuardRoute>} />
        {/* Import History moved into Logs; kept as a redirect for old links/bookmarks */}
        <Route path="fa/import/history" element={<Navigate to="/boss/logs" replace />} />
        <Route path="fa/import/reconciliation/:batchId" element={<NavGuardRoute path="/boss/fa/import/history"><ImportReconciliationPage /></NavGuardRoute>} />
        <Route path="fa/invoices" element={<NavGuardRoute path="/boss/fa/invoices"><InvoicesPage /></NavGuardRoute>} />
        <Route path="fa/invoices/:id" element={<NavGuardRoute path="/boss/fa/invoices"><InvoiceDetailPage /></NavGuardRoute>} />
        <Route path="fa/missing" element={<NavGuardRoute path="/boss/fa/missing"><MissingInvoicesPage /></NavGuardRoute>} />
        {/* Sales & CRM */}
        <Route path="scrm/parties" element={<NavGuardRoute path="/boss/scrm/parties"><PartiesPage /></NavGuardRoute>} />
        <Route path="scrm/salesmen" element={<NavGuardRoute path="/boss/scrm/salesmen"><SalesmenPage /></NavGuardRoute>} />
        <Route path="scrm/products" element={<NavGuardRoute path="/boss/scrm/products"><ProductsPage /></NavGuardRoute>} />
        {/* Supply Chain */}
        <Route path="scl/transports" element={<NavGuardRoute path="/boss/scl/transports"><TransportsPage /></NavGuardRoute>} />
        <Route path="scl/stations" element={<NavGuardRoute path="/boss/scl/stations"><StationsPage /></NavGuardRoute>} />
        {/* Bilty Docs — merges the old Bilty Upload and Document Center pages.
            They were gated by different permissions (SCL_AGENT has bilty but
            not documents; SCRM_PARTY the reverse), so the guard here accepts
            either, and BiltyDocsPage itself only renders the section(s) the
            user actually has. */}
        <Route path="scl/bilty" element={<NavGuardRoute path={["/boss/scl/bilty", "/boss/dm/documents"]}><BiltyDocsPage /></NavGuardRoute>} />
        {/* WhatsApp Logs moved into Logs; kept as a redirect for old links/bookmarks */}
        <Route path="scl/whatsapp" element={<Navigate to="/boss/logs" replace />} />
        {/* Logs — merges Import History (fa/import/history, BOSS_ADMIN-only
            today) and WhatsApp Logs (scl/whatsapp, also SCL_AGENT). Same
            per-section permission gating as Bilty Docs above. */}
        <Route path="logs" element={<NavGuardRoute path={["/boss/fa/import/history", "/boss/scl/whatsapp"]}><LogsPage /></NavGuardRoute>} />
        {/* Analytics — merged into the home dashboard; kept as a redirect for old links/bookmarks */}
        <Route path="bia/dashboard" element={<Navigate to="/boss/home" replace />} />
        {/* Documents — merged into Bilty Docs; kept as a redirect for old links/bookmarks */}
        <Route path="dm/documents" element={<Navigate to="/boss/scl/bilty" replace />} />
        {/* SSP Self-Service Portal */}
        <Route path="ssp/portal" element={<MyPortalPage />} />
        {/* System (Admin only) */}
        <Route path="settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
        <Route path="settings/users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to={user ? "/boss/home" : "/login"} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
